import clsx from "clsx";
import { ReactNode, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Warning } from "@/assets/icons/ds";
import { DireProgressBar } from "@/components/Layout/ProgressBar/DireProgressBar";
import {
  RoamingInfoRoamingStatus,
  RoamingInfoUsageZone,
} from "@/services/model";
import {
  formatRoamingTotalInGb,
  formatRoamingUsageInGb,
  isZone1,
} from "@/utils/deviceUtils";
import { Status } from "@/utils/enums";

import { RoamingCallExplanation } from "./RoamingCallExplanation";
import { TarifBarHeadline } from "./TarifBarHeadline";

type RoamingUsageProps = {
  days: number;
  description: string;
  descriptionExtension?: string;
  /** Additional note shown alongside descriptionExtension, e.g. the pro-rata
   * first-month explanation - the two are independent and can both show at once. */
  partialHint?: ReactNode;
  initial: number;
  isFirstOfUsageZone: boolean;
  remaining: number;
  consumed?: number;
  title: string;
  isIncludedInService?: boolean;
  roamingStatus?: RoamingInfoRoamingStatus;
  isYearly?: boolean;
  showFlatMobileExplanation?: boolean;
  showRoamingCallExplanation?: boolean;
  isNotAllowedToUse?: boolean;
  shouldTreatAsActive: boolean;
  hasIncludedRoamingCalls: boolean;
  isRoamingCallType?: boolean;
  /** Roaming zone - used to determine display format (GB for Zone 1, MB for Zone 2/3) */
  zone?: RoamingInfoUsageZone | string;
  /** Whether this is displaying data usage (vs minutes or days) */
  isDataUsage?: boolean;
  /** Leading tarif-bar icon (e.g. Signal for data, Globe for calls). Omit for icon-less rows such as runtime/duration. */
  icon?: ReactNode;
};

const getPercentage = (
  initial: number | undefined,
  remaining: number | undefined,
) => {
  if (initial !== undefined && remaining !== undefined && initial > 0) {
    // Remaining capacity: full when unused, depletes as GB/min are consumed.
    return Math.min(100, Math.max(0, (remaining / initial) * 100));
  }

  return 0;
};

/** A text row rendered below a tarif-bar usage row, e.g. a renewal or exhausted-usage hint. */
export const NoteRow = ({
  icon,
  children,
}: {
  icon?: ReactNode;
  children: ReactNode;
}) => (
  <div className="flex w-full items-start gap-3">
    {icon}
    <div className="flex-1 text-body-xs leading-none text-foreground-primary">
      {children}
    </div>
  </div>
);

/**
 * Calculates and displays the data usage of a roaming option.
 * TODO: Should be split into smaller components later on
 */
export const RoamingUsage = ({
  days,
  description,
  descriptionExtension,
  partialHint,
  initial,
  isFirstOfUsageZone,
  remaining,
  consumed,
  title,
  isIncludedInService,
  roamingStatus,
  isYearly,
  showFlatMobileExplanation,
  showRoamingCallExplanation,
  isNotAllowedToUse,
  shouldTreatAsActive,
  hasIncludedRoamingCalls,
  isRoamingCallType,
  zone,
  isDataUsage,
  icon,
}: RoamingUsageProps) => {
  const [usage, setUsage] = useState<number | undefined>(0);
  const { t } = useTranslation("portal");

  // For data usage: Zone 1 displays in GB, Zone 2/3 display in MB
  const useGB = isDataUsage && isZone1(zone);

  const isActive = roamingStatus === Status.active || shouldTreatAsActive;

  useEffect(() => {
    setUsage(isActive ? getPercentage(initial, remaining) : undefined);
  }, [roamingStatus, isActive, initial, remaining]);

  const isUsedUpMobileAboRoaming = isIncludedInService && remaining === 0;
  const isOverspentMobileAboRoaming = isIncludedInService && remaining < 0;
  const isFlatMobileRoamingCallCase =
    isRoamingCallType && hasIncludedRoamingCalls;

  const PackageExplanation = () => {
    if (showFlatMobileExplanation && !isUsedUpMobileAboRoaming) {
      return <MobileAboProRataExplanation title={title} />;
    } else if (showRoamingCallExplanation && !isNotAllowedToUse) {
      return <RoamingCallExplanation title={title} />;
    } else if (showRoamingCallExplanation && isNotAllowedToUse) {
      return (
        <TarifBarHeadline
          title={title}
          tooltipContent={
            <p>
              {t(
                "cockpit.managementTile.roaming.showOption.active.notAllowedTooltip",
              )}
            </p>
          }
        />
      );
    } else {
      return <TarifBarHeadline title={title} />;
    }
  };

  return (
    <div
      className={clsx(
        "flex w-full flex-col gap-3 rounded-md bg-surface-quaternary p-4",
        isUsedUpMobileAboRoaming && "opacity-50",
      )}
    >
      <div className="flex w-full items-start gap-3">
        {icon}
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <PackageExplanation />

          <div className="flex w-full flex-col gap-2">
            <DireProgressBar
              value={
                isNotAllowedToUse ||
                (roamingStatus === RoamingInfoRoamingStatus.ready &&
                  consumed === 0)
                  ? undefined
                  : usage
              }
            />

            <span
              className={clsx(
                "text-body-sm font-semibold leading-none",
                isActive
                  ? "text-foreground-primary"
                  : "text-foreground-secondary",
              )}
            >
              {roamingStatus === "ordered" && (
                <p>
                  {t("cockpit.managementTile.roaming.showOption.ordered.usage")}
                </p>
              )}

              {roamingStatus === Status.ready &&
                !isFlatMobileRoamingCallCase && (
                  <Trans
                    components={[<span key="0" className="font-semibold" />]}
                    t={t}
                    values={{
                      days,
                    }}
                  >
                    {isFirstOfUsageZone
                      ? "cockpit.managementTile.roaming.showOption.ready.firstPackageOfUsageZone.usage"
                      : "cockpit.managementTile.roaming.showOption.ready.notFirstPackageOfUsageZone.usage"}
                  </Trans>
                )}
              {(roamingStatus === Status.active ||
                isFlatMobileRoamingCallCase) && (
                <span>
                  {isNotAllowedToUse ? (
                    <span>
                      {t(
                        "cockpit.managementTile.roaming.showOption.active.notAllowed",
                      )}
                    </span>
                  ) : (
                    <Trans
                      components={[<span key="0" className="font-semibold" />]}
                      values={{
                        // These messages all read "X of Y available/verfügbar",
                        // so the displayed value is always what's left, not
                        // what's been used.
                        value: useGB
                          ? formatRoamingUsageInGb(remaining)
                          : remaining,
                        initial: useGB
                          ? formatRoamingTotalInGb(initial)
                          : initial,
                      }}
                      t={t}
                    >
                      {useGB ? `${description}GB` : description}
                    </Trans>
                  )}
                </span>
              )}
            </span>

            {isOverspentMobileAboRoaming && (
              <NoteRow icon={<Warning className="size-6 shrink-0" />}>
                <Trans
                  components={[
                    <Link
                      key="0"
                      className="underline"
                      to={t(
                        "cockpit.managementTile.roaming.showOption.active.overspentCallsLink",
                      )}
                      target="_blank"
                    />,
                  ]}
                  t={t}
                >
                  {t(
                    "cockpit.managementTile.roaming.showOption.active.overspentCalls",
                  )}
                </Trans>
              </NoteRow>
            )}

            {!isYearly && descriptionExtension && (
              <p className="text-body-xs leading-none text-foreground-primary">
                {descriptionExtension}
              </p>
            )}

            {!isYearly && partialHint && (
              <div className="text-body-xs leading-none text-foreground-primary">
                {partialHint}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MobileAboProRataExplanation = ({ title }: { title: string }) => {
  const { t } = useTranslation("portal");

  return (
    <TarifBarHeadline
      title={title}
      tooltipContent={
        <>
          <h3 className="mb-1 block font-semibold">
            {t(
              "cockpit.managementTile.roaming.showOption.flatMobileTooltip.title",
            )}
          </h3>
          <ul className="list-disc pl-4">
            <li>
              <b>
                {t(
                  "cockpit.managementTile.roaming.showOption.flatMobileTooltip.monthlyRenewal.title",
                )}
              </b>
              <br />
              {t(
                "cockpit.managementTile.roaming.showOption.flatMobileTooltip.monthlyRenewal.description",
              )}
            </li>
            <li>
              <b>
                {t(
                  "cockpit.managementTile.roaming.showOption.flatMobileTooltip.proRataVolume.title",
                )}
              </b>
              <br />
              {t(
                "cockpit.managementTile.roaming.showOption.flatMobileTooltip.proRataVolume.description",
              )}
            </li>
          </ul>
          <p className="pt-2">
            <b>
              {t(
                "cockpit.managementTile.roaming.showOption.flatMobileTooltip.example.title",
              )}
            </b>
            <br />
            {t(
              "cockpit.managementTile.roaming.showOption.flatMobileTooltip.example.description",
            )}
          </p>
        </>
      }
    />
  );
};
